import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, MailOpen, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages({ token }) {
  const [items, setItems] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/contact/messages`, { headers: { "X-Admin-Token": token } });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/contact/messages/${id}/read`, {}, { headers: { "X-Admin-Token": token } });
      setItems((arr) => arr.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch {
      toast.error("Update failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/contact/messages/${id}`, { headers: { "X-Admin-Token": token } });
      setItems((arr) => arr.filter((m) => m.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (items === null) return <div className="font-mono text-xs text-neutral-500 py-10">Loading messages…</div>;

  const unread = items.filter((m) => !m.read).length;

  return (
    <div data-testid="admin-messages-panel">
      <div className="flex items-center gap-3 mb-6">
        <Inbox size={16} className="text-[#ff4500]" />
        <h2 className="font-serif text-2xl font-light">Contact Messages</h2>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          — {items.length} total · {unread} unread
        </span>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[#262626] py-16 text-center font-mono text-xs text-neutral-600 uppercase tracking-widest">
          No messages yet
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div
              key={m.id}
              data-testid={`message-${m.id}`}
              className={`border p-5 bg-[#0f0f0f] ${m.read ? "border-[#1a1a1a] opacity-70" : "border-[#ff4500]/50"}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <span className="text-white font-medium">{m.name}</span>
                  <span className="text-neutral-500 text-sm ml-3">{m.email}</span>
                  {m.company && <span className="text-neutral-500 text-sm ml-3">· {m.company}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-neutral-500">
                    {new Date(m.created_at).toLocaleString()}
                  </span>
                  {!m.read && (
                    <button
                      data-testid={`message-read-${m.id}`}
                      onClick={() => markRead(m.id)}
                      title="Mark as read"
                      className="p-2 border border-[#262626] hover:border-[#ff4500] text-neutral-400 hover:text-[#ff4500]"
                    >
                      <MailOpen size={13} />
                    </button>
                  )}
                  <button
                    data-testid={`message-delete-${m.id}`}
                    onClick={() => del(m.id)}
                    title="Delete"
                    className="p-2 border border-[#262626] hover:border-red-500 text-red-500"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {m.subject && (
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#ff4500] mb-2">{m.subject}</div>
              )}
              <p className="text-neutral-300 text-sm leading-relaxed whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
